/**
 * Create Service Form Component
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ImageUpload, IMAGE_FORMATS } from '../ui/ImageUpload';
import { ChevronRight, Info, MapPin } from 'lucide-react';
import axios from 'axios';
import { AVAILABLE_COUNTRIES } from '../../data/postalCodes';
import { 
  API_URL, 
  SERVICE_TYPES, 
  TARGET_AUDIENCE, 
  lookupPostalCodeWithGeo, 
  lookupCityToPostalCode 
} from './formUtils';

export function CreateServiceForm({ editMode = false, existingService = null }) {
  const { t, i18n } = useTranslation();
  const { getAuthHeaders, user } = useAuthStore();
  const navigate = useNavigate();
  const language = user?.preferred_language || i18n.language || 'es';
  const [isLoading, setIsLoading] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  
  const [serviceImage, setServiceImage] = useState(existingService?.images?.[0] || null);
  const [gallery, setGallery] = useState(existingService?.gallery || []);
  const [serviceType, setServiceType] = useState(existingService?.service_type || 'tantric_massage');
  const [title, setTitle] = useState(existingService?.title || { es: '', en: '', it: '' });
  const [description, setDescription] = useState(existingService?.description || { es: '', en: '', it: '' });
  const [price, setPrice] = useState(existingService?.price?.toString() || '');
  const [duration, setDuration] = useState(existingService?.duration_minutes?.toString() || '60');
  // Follow-up session pricing
  const [followupPrice, setFollowupPrice] = useState(existingService?.followup_price?.toString() || '');
  const [followupDuration, setFollowupDuration] = useState(existingService?.followup_duration_minutes?.toString() || '');
  const [targetAudience, setTargetAudience] = useState(existingService?.target_audience || ['all']);
  const [country, setCountry] = useState(existingService?.location?.country || 'España');
  const [postalCode, setPostalCode] = useState(existingService?.location?.postal_code || '');
  const [city, setCity] = useState(existingService?.location?.city || '');
  const [province, setProvince] = useState(existingService?.location?.province || '');
  const [area, setArea] = useState(existingService?.location?.area || '');
  const [address, setAddress] = useState(existingService?.location?.address || '');
  const [cityAutoFilled, setCityAutoFilled] = useState(!!existingService?.location?.city);
  const [coordinates, setCoordinates] = useState({ 
    lat: existingService?.location?.latitude || 0, 
    lng: existingService?.location?.longitude || 0 
  });
  const [geoError, setGeoError] = useState('');
  const [nearestCodes, setNearestCodes] = useState([]);
  const [skipInitialLookup, setSkipInitialLookup] = useState(editMode);
  const [multipleCities, setMultipleCities] = useState([]);
  const [showCitySelector, setShowCitySelector] = useState(false);

  // Gallery functions
  const addGalleryImage = (url) => {
    if (gallery.length < 10) {
      setGallery([...gallery, { url, caption: '' }]);
    }
  };
  
  const removeGalleryImage = (index) => {
    setGallery(gallery.filter((_, i) => i !== index));
  };
  
  const updateGalleryCaption = (index, caption) => {
    const newGallery = [...gallery];
    newGallery[index] = { ...newGallery[index], caption };
    setGallery(newGallery);
  };

  // Clear validation error when field changes
  const clearError = (field) => {
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const toggleTargetAudience = (key) => {
    if (key === 'all') {
      setTargetAudience(targetAudience.includes('all') ? [] : ['all']);
    } else {
      let newAudience = targetAudience.filter(a => a !== 'all');
      if (newAudience.includes(key)) {
        newAudience = newAudience.filter(a => a !== key);
      } else {
        newAudience.push(key);
      }
      setTargetAudience(newAudience);
    }
  };

  useEffect(() => {
    const doLookup = async () => {
      if (skipInitialLookup) {
        setSkipInitialLookup(false);
        return;
      }
      
      const isSpain = ['españa', 'spain', 'spagna', 'espanha', 'spanien'].includes(country.toLowerCase().trim());
      
      if (isSpain && postalCode.length === 5) {
        setIsLookingUp(true);
        setGeoError('');
        setNearestCodes([]);
        setMultipleCities([]);
        setShowCitySelector(false);
        
        const result = await lookupPostalCodeWithGeo(postalCode, 'España');
        
        if (result.found) {
          // Check if this postal code has multiple cities
          if (result.hasMultipleCities && result.cities?.length > 1) {
            setMultipleCities(result.cities);
            setShowCitySelector(true);
            setProvince(result.province || '');
            setArea(result.area || '');
            setCity(''); // Clear city to force user selection
            setCityAutoFilled(false);
            setGeoError('');
          } else {
            // Single city - auto-fill normally
            setCity(result.city);
            setProvince(result.province || '');
            setArea(result.area || '');
            
            if (address.trim()) {
              try {
                const fullAddress = `${address}, ${result.city}, ${postalCode}, España`;
                const nominatimUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=1`;
                const response = await fetch(nominatimUrl);
                const data = await response.json();
                
                if (data && data.length > 0) {
                  setCoordinates({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
                } else {
                  setCoordinates({ lat: result.lat, lng: result.lng });
                }
              } catch (e) {
                setCoordinates({ lat: result.lat, lng: result.lng });
              }
            } else {
              setCoordinates({ lat: result.lat, lng: result.lng });
            }
            
            setCityAutoFilled(true);
            setGeoError('');
          }
        } else {
          setCityAutoFilled(false);
          setCoordinates({ lat: 0, lng: 0 });
          setNearestCodes(result.nearestCodes || []);
          setGeoError(language === 'es' 
            ? 'Código postal no encontrado. La geolocalización es obligatoria.'
            : 'Postal code not found. Geolocation is required.');
        }
        setIsLookingUp(false);
      } else {
        setCityAutoFilled(false);
        setGeoError('');
        setMultipleCities([]);
        setShowCitySelector(false);
      }
    };
    
    const timeoutId = setTimeout(doLookup, 500);
    return () => clearTimeout(timeoutId);
  }, [postalCode, country, language, skipInitialLookup, address]);

  // Handle city selection from multiple options
  const handleCitySelect = (selectedCity) => {
    setCity(selectedCity.city);
    setCoordinates({ lat: selectedCity.lat, lng: selectedCity.lng });
    setCityAutoFilled(true);
    setShowCitySelector(false);
    setGeoError('');
  };

  const labels = {
    es: {
      back: 'Volver',
      title: editMode ? 'Editar Servicio' : 'Crear Servicio',
      servicePhoto: 'Foto Principal del Servicio',
      addPhoto: 'Añade una foto para tu servicio',
      gallery: 'Galería de Fotos',
      galleryDesc: 'Añade hasta 10 fotos adicionales (opcional)',
      addToGallery: 'Añadir foto a la galería',
      caption: 'Descripción de la foto (opcional)',
      captionPlaceholder: 'Ej: Espacio de trabajo, técnica específica...',
      serviceType: 'Tipo de Servicio',
      serviceTitle: 'Título',
      serviceDescription: 'Descripción (opcional)',
      enterTitle: 'Introduce el título del servicio',
      enterDescription: 'Describe tu servicio en detalle...',
      price: 'Precio primera sesión (EUR)',
      duration: 'Duración primera sesión (min)',
      followupSection: 'Precio sesiones sucesivas',
      followupSectionDesc: '¿Ya eres cliente? Ofrece un precio especial para las siguientes sesiones',
      followupPrice: 'Precio (EUR)',
      followupDuration: 'Duración (min)',
      reservedFor: 'Reservado a',
      location: 'Ubicación',
      country: 'País',
      postalCode: 'Código Postal',
      city: 'Ciudad',
      cityAutoPlaceholder: 'Se completa automáticamente con el C.P.',
      selectCity: 'Selecciona tu ciudad',
      multipleCitiesFound: 'Este código postal corresponde a varias ciudades:',
      address: 'Dirección (opcional)',
      addressPlaceholder: 'Ej: Calle Mayor 15, 2º B',
      autoFilled: '(auto)',
      geoDetected: 'Geolocalización detectada automáticamente',
      lookingUp: 'Buscando ubicación...',
      nearestCodes: 'Códigos postales cercanos:',
      geoRequired: 'La geolocalización es obligatoria para publicar tu servicio.',
      infoText: editMode 
        ? 'Las modificaciones serán revisadas por un administrador antes de ser visibles.'
        : 'Tu servicio será revisado por un administrador antes de ser visible para los usuarios.',
      submit: editMode ? 'Guardar Cambios' : 'Enviar para Aprobación',
      submitting: 'Guardando...',
      required: 'obligatorio',
      requiredFields: 'Por favor completa todos los campos obligatorios',
      photoRequired: 'La foto principal es obligatoria',
      titleRequired: 'El título es obligatorio',
      typeRequired: 'El tipo de servicio es obligatorio',
      audienceRequired: 'Debes indicar a quién está reservado',
      countryRequired: 'El país es obligatorio',
      postalCodeRequired: 'El código postal es obligatorio',
      cityRequired: 'La ciudad es obligatoria',
      geoRequiredAlert: 'La geolocalización es obligatoria. Por favor introduce un código postal válido.',
      successMsg: editMode 
        ? 'Cambios guardados. Pendientes de aprobación del administrador.'
        : 'Tu servicio ha sido enviado para aprobación del administrador.',
      errorMsg: editMode ? 'Error al guardar los cambios' : 'Error al crear el servicio',
    },
    en: {
      back: 'Back',
      title: editMode ? 'Edit Service' : 'Create Service',
      servicePhoto: 'Main Service Photo',
      addPhoto: 'Add a photo for your service',
      gallery: 'Photo Gallery',
      galleryDesc: 'Add up to 10 additional photos (optional)',
      addToGallery: 'Add photo to gallery',
      caption: 'Photo description (optional)',
      captionPlaceholder: 'E.g.: Workspace, specific technique...',
      serviceType: 'Service Type',
      serviceTitle: 'Title',
      serviceDescription: 'Description (optional)',
      enterTitle: 'Enter service title',
      enterDescription: 'Describe your service in detail...',
      price: 'First session price (EUR)',
      duration: 'First session duration (min)',
      followupSection: 'Follow-up sessions pricing',
      followupSectionDesc: 'Already a client? Offer a special price for subsequent sessions',
      followupPrice: 'Price (EUR)',
      followupDuration: 'Duration (min)',
      reservedFor: 'Reserved for',
      location: 'Location',
      country: 'Country',
      postalCode: 'Postal Code',
      city: 'City',
      cityAutoPlaceholder: 'Auto-filled from postal code',
      selectCity: 'Select your city',
      multipleCitiesFound: 'This postal code covers multiple cities:',
      address: 'Address (optional)',
      addressPlaceholder: 'E.g.: 15 Main Street, Apt 2B',
      autoFilled: '(auto)',
      geoDetected: 'Geolocation detected automatically',
      lookingUp: 'Looking up location...',
      nearestCodes: 'Nearest postal codes:',
      geoRequired: 'Geolocation is required to publish your service.',
      infoText: editMode
        ? 'Changes will be reviewed by an administrator before becoming visible.'
        : 'Your service will be reviewed by an administrator before it becomes visible to users.',
      submit: editMode ? 'Save Changes' : 'Submit for Approval',
      submitting: 'Saving...',
      required: 'required',
      requiredFields: 'Please complete all required fields',
      photoRequired: 'Main photo is required',
      titleRequired: 'Title is required',
      typeRequired: 'Service type is required',
      audienceRequired: 'You must indicate who it is reserved for',
      countryRequired: 'Country is required',
      postalCodeRequired: 'Postal code is required',
      cityRequired: 'City is required',
      geoRequiredAlert: 'Geolocation is required. Please enter a valid postal code.',
      successMsg: editMode
        ? 'Changes saved. Pending admin approval.'
        : 'Your service has been submitted for admin approval.',
      errorMsg: editMode ? 'Failed to save changes' : 'Failed to create service',
    }
  };
  
  const l = labels[language] || labels.es;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = {};
    
    if (!serviceImage) {
      errors.serviceImage = l.photoRequired;
    }
    if (!serviceType) {
      errors.serviceType = l.typeRequired;
    }
    if (!title[language]?.trim()) {
      errors.title = l.titleRequired;
    }
    if (targetAudience.length === 0) {
      errors.targetAudience = l.audienceRequired;
    }
    if (!country?.trim()) {
      errors.country = l.countryRequired;
    }
    if (!postalCode?.trim()) {
      errors.postalCode = l.postalCodeRequired;
    }
    if (!city?.trim()) {
      errors.city = l.cityRequired;
    }
    if (coordinates.lat === 0 && coordinates.lng === 0) {
      errors.geo = l.geoRequiredAlert;
    }
    
    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    setIsLoading(true);
    try {
      const payload = {
        service_type: serviceType,
        title,
        description,
        price: price ? parseFloat(price) : null,
        currency: 'EUR',
        duration_minutes: parseInt(duration) || 60,
        // Follow-up session pricing (only include if filled)
        followup_price: followupPrice ? parseFloat(followupPrice) : null,
        followup_duration_minutes: followupDuration ? parseInt(followupDuration) : null,
        target_audience: targetAudience.length > 0 ? targetAudience : ['all'],
        images: serviceImage ? [serviceImage] : [],
        gallery: gallery,
        location: {
          city,
          province,
          area,
          country,
          address: address.trim(),
          postal_code: postalCode,
          latitude: coordinates.lat,
          longitude: coordinates.lng,
        },
      };
      
      if (editMode && existingService) {
        await axios.put(
          `${API_URL}/api/services/${existingService.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
      } else {
        await axios.post(
          `${API_URL}/api/services`,
          payload,
          { headers: getAuthHeaders() }
        );
      }
      
      alert(l.successMsg);
      navigate('/profile');
    } catch (error) {
      alert(error.response?.data?.detail || l.errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const RequiredBadge = () => (
    <span className="inline-flex items-center justify-center px-2 py-0.5 ml-2 text-xs font-medium bg-red-100 text-red-700 rounded">
      {language === 'es' ? 'Obligatorio' : 'Required'}
    </span>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in" data-testid="create-service-page">
      <button onClick={() => navigate(-1)} className="flex items-center text-secondary mb-6 hover:text-gold">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> {l.back}
      </button>
      
      <h1 className="font-serif text-3xl font-bold text-primary mb-6">{l.title}</h1>
      
      {/* Validation Errors Summary */}
      {Object.keys(validationErrors).length > 0 && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="font-medium text-red-800 mb-2">{l.requiredFields}:</p>
          <ul className="list-disc list-inside space-y-1">
            {Object.values(validationErrors).map((error, idx) => (
              <li key={idx} className="text-sm text-red-600">{error}</li>
            ))}
          </ul>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 space-y-6">
        {/* Service Image - REQUIRED */}
        <div className={validationErrors.serviceImage ? 'ring-2 ring-red-300 rounded-xl p-2 -m-2' : ''}>
          <div className="flex items-center mb-2">
            <label className="text-sm font-semibold text-primary">{l.servicePhoto}</label>
            <RequiredBadge />
          </div>
          <ImageUpload
            image={serviceImage}
            onImageChange={(url) => { setServiceImage(url); clearError('serviceImage'); }}
            label=""
            aspectRatio={IMAGE_FORMATS.SERVICE.aspectRatio}
            maxSize={IMAGE_FORMATS.SERVICE.maxSize}
            shape="rectangle"
            placeholder={l.addPhoto}
            language={language}
            folder="services/"
          />
          {validationErrors.serviceImage && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.serviceImage}</p>
          )}
        </div>
        
        {/* Photo Gallery */}
        <div className="border-t pt-6">
          <label className="block text-sm font-semibold text-primary mb-2">{l.gallery}</label>
          <p className="text-sm text-secondary mb-4">{l.galleryDesc}</p>
          
          {gallery.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              {gallery.map((item, index) => (
                <div key={item.url || `gallery-${index}`} className="relative group">
                  <div className="aspect-square rounded-xl overflow-hidden bg-gray-100">
                    <img src={item.url} alt="" className="w-full h-full object-cover" />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeGalleryImage(index)}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    ×
                  </button>
                  <Input
                    placeholder={l.captionPlaceholder}
                    value={item.caption}
                    onChange={(e) => updateGalleryCaption(index, e.target.value)}
                    className="mt-2 text-sm"
                  />
                </div>
              ))}
            </div>
          )}
          
          {gallery.length < 10 && (
            <ImageUpload
              image={null}
              onImageChange={(url) => addGalleryImage(url)}
              label=""
              aspectRatio={IMAGE_FORMATS.SERVICE.aspectRatio}
              maxSize={IMAGE_FORMATS.SERVICE.maxSize}
              shape="rectangle"
              placeholder={l.addToGallery}
              language={language}
              folder="services/gallery/"
              showPreview={false}
            />
          )}
          
          {gallery.length >= 10 && (
            <p className="text-sm text-amber-600">Has alcanzado el máximo de 10 fotos en la galería.</p>
          )}
        </div>
        
        {/* Service Type Selection */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-3">{l.serviceType} *</label>
          <div className="grid grid-cols-2 gap-3">
            {SERVICE_TYPES.map((type) => (
              <button
                key={type.key}
                type="button"
                onClick={() => setServiceType(type.key)}
                className={`p-4 rounded-xl border-2 text-center transition-all ${
                  serviceType === type.key 
                    ? 'border-gold bg-gold text-white' 
                    : 'border-gray-200 hover:border-gold'
                }`}
                style={serviceType === type.key ? { backgroundColor: '#D4A574', borderColor: '#D4A574' } : {}}
                data-testid={`service-type-${type.key}`}
              >
                <span className="text-2xl block mb-2">{type.icon}</span>
                <span className="text-sm font-medium">{type.label[language] || type.label.es}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Title */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{l.serviceTitle} ({language.toUpperCase()}) *</label>
          <Input
            placeholder={l.enterTitle}
            value={title[language]}
            onChange={(e) => setTitle({ ...title, [language]: e.target.value })}
            required
            data-testid="service-title"
          />
        </div>
        
        {/* Description */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{l.serviceDescription} ({language.toUpperCase()}) *</label>
          <Textarea
            placeholder={l.enterDescription}
            value={description[language]}
            onChange={(e) => setDescription({ ...description, [language]: e.target.value })}
            rows={4}
            required
            data-testid="service-description"
          />
        </div>
        
        {/* Target Audience */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-3">{l.reservedFor}</label>
          <div className="flex flex-wrap gap-2">
            {TARGET_AUDIENCE.map((audience) => (
              <button
                key={audience.key}
                type="button"
                onClick={() => toggleTargetAudience(audience.key)}
                className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                  targetAudience.includes(audience.key)
                    ? 'border-gold bg-gold text-white'
                    : 'border-gray-200 text-secondary hover:border-gold'
                }`}
                style={targetAudience.includes(audience.key) ? { backgroundColor: '#D4A574', borderColor: '#D4A574' } : {}}
                data-testid={`audience-${audience.key}`}
              >
                {audience.label[language] || audience.label.es}
              </button>
            ))}
          </div>
        </div>
        
        {/* Price & Duration */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{l.price}</label>
            <Input
              type="number"
              placeholder="0.00"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              min={0}
              data-testid="price"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{l.duration}</label>
            <Input
              type="number"
              placeholder="60"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              min={15}
              data-testid="duration"
            />
          </div>
        </div>
        
        {/* Follow-up Sessions Pricing (Optional) */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <span className="text-amber-600 text-lg">✨</span>
            </div>
            <div>
              <h4 className="font-semibold text-amber-800">{l.followupSection}</h4>
              <p className="text-sm text-amber-700">{l.followupSectionDesc}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-amber-800 mb-2">{l.followupPrice}</label>
              <Input
                type="number"
                placeholder="0.00"
                value={followupPrice}
                onChange={(e) => setFollowupPrice(e.target.value)}
                min={0}
                className="bg-white border-amber-200 focus:border-amber-400"
                data-testid="followup-price"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-amber-800 mb-2">{l.followupDuration}</label>
              <Input
                type="number"
                placeholder="60"
                value={followupDuration}
                onChange={(e) => setFollowupDuration(e.target.value)}
                min={15}
                className="bg-white border-amber-200 focus:border-amber-400"
                data-testid="followup-duration"
              />
            </div>
          </div>
        </div>
        
        {/* Location */}
        <h3 className="font-semibold text-primary pt-4 border-t">{l.location} *</h3>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{l.country} *</label>
          <Select value={country} onValueChange={(v) => setCountry(v)}>
            <SelectTrigger className="w-full" data-testid="country">
              <SelectValue placeholder="Selecciona país" />
            </SelectTrigger>
            <SelectContent>
              {AVAILABLE_COUNTRIES.map(c => (
                <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{l.postalCode} *</label>
            <Input 
              placeholder="Ej: 28001" 
              value={postalCode} 
              onChange={(e) => setPostalCode(e.target.value)} 
              maxLength={5} 
              required 
              data-testid="postal-code"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">
              {l.city} * {cityAutoFilled && <span className="text-green-600 text-xs">{l.autoFilled}</span>}
            </label>
            
            {/* City selector when multiple cities available */}
            {showCitySelector && multipleCities.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-amber-600 font-medium">{l.multipleCitiesFound}</p>
                <div className="grid gap-2">
                  {multipleCities.map((cityOption) => (
                    <button
                      key={`city-${cityOption.city}-${cityOption.province || ''}`}
                      type="button"
                      onClick={() => handleCitySelect(cityOption)}
                      className="text-left p-3 border rounded-lg hover:bg-amber-50 hover:border-amber-300 transition-colors"
                      data-testid={`city-option-${cityOption.city}`}
                    >
                      <span className="font-medium">{cityOption.city}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <Input 
                placeholder={l.cityAutoPlaceholder || 'Compilato automaticamente dal CAP'}
                value={city} 
                readOnly
                required 
                className="bg-gray-100 cursor-not-allowed border-gray-200"
                data-testid="city"
              />
            )}
          </div>
        </div>
        
        {/* Address (optional) */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{l.address}</label>
          <Input 
            placeholder={l.addressPlaceholder}
            value={address} 
            onChange={(e) => setAddress(e.target.value)} 
            data-testid="address"
          />
        </div>
        
        {/* Geolocation Status */}
        {isLookingUp && (
          <p className="text-xs text-blue-600 flex items-center gap-1">
            <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            {l.lookingUp}
          </p>
        )}
        {cityAutoFilled && coordinates.lat !== 0 && (
          <p className="text-xs text-green-600 flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {l.geoDetected}
          </p>
        )}
        {geoError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs text-red-600 font-medium">{geoError}</p>
            {nearestCodes.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-red-500">{l.nearestCodes}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {nearestCodes.map(code => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setPostalCode(code)}
                      className="px-2 py-0.5 bg-white border border-red-300 rounded text-xs text-red-600 hover:bg-red-100"
                    >
                      {code} ({lookupPostalCode(code)?.city || 'Unknown'})
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Info Box */}
        <div className="flex gap-3 p-4 rounded-xl" style={{ backgroundColor: '#E8F4FD' }}>
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />
          <p className="text-sm text-blue-700">{l.infoText}</p>
        </div>
        
        <Button 
          type="submit" 
          disabled={isLoading || (coordinates.lat === 0 && coordinates.lng === 0)} 
          className="w-full bg-gold text-white py-3 disabled:opacity-50 disabled:cursor-not-allowed" 
          style={{ backgroundColor: '#D4A574' }} 
          data-testid="submit-service"
        >
          {isLoading ? l.submitting : l.submit}
        </Button>
      </form>
    </div>
  );
}
